import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MousePointer2, Play, Sparkles } from "lucide-react";

/**
 * Self-playing notation bento cover.
 * Desktop: transcript + four stacked SOAP panels.
 * Mobile: transcript + one SOAP card with S/O/A/P tabs.
 *
 * Transcript stops after the tendinopathy assessment line. Assessment and Plan
 * start empty; a purple glitter icon is "clicked", becomes a waveform, then
 * the panel fills instantly.
 */

interface Event {
  target: 0 | 1 | 2 | 3;
  speaker: "Clinician" | "Patient";
  text: string;
  fill: number;
}

/** Transcript only through Subjective + Objective + one Assessment cue. */
const EVENTS: Event[] = [
  { target: 0, speaker: "Patient", text: "Here for right shoulder pain.", fill: 1 },
  { target: 0, speaker: "Patient", text: "Started a month ago, gradual.", fill: 2 },
  { target: 0, speaker: "Patient", text: "Front of the shoulder, dull ache.", fill: 3 },
  { target: 0, speaker: "Patient", text: "Worse when I reach overhead.", fill: 4 },
  { target: 1, speaker: "Clinician", text: "Flexion: right 142, left 150.", fill: 1 },
  { target: 1, speaker: "Clinician", text: "Abduction: right 130, left 138.", fill: 2 },
  { target: 1, speaker: "Clinician", text: "Internal rotation 58 and 65.", fill: 3 },
  { target: 1, speaker: "Clinician", text: "External rotation 70 and 78.", fill: 4 },
  { target: 2, speaker: "Clinician", text: "Consistent with cuff tendinopathy.", fill: 0 },
];

type Phase =
  | { kind: "talk"; event: number }
  | { kind: "ai-idle"; panel: 2 | 3 }
  | { kind: "ai-click"; panel: 2 | 3 }
  | { kind: "ai-wave"; panel: 2 | 3 }
  | { kind: "ai-filled"; panel: 2 | 3 }
  | { kind: "hold" };

const PHASES: Phase[] = [
  ...EVENTS.map((_, i): Phase => ({ kind: "talk", event: i })),
  { kind: "ai-idle", panel: 2 },
  { kind: "ai-click", panel: 2 },
  { kind: "ai-wave", panel: 2 },
  { kind: "ai-filled", panel: 2 },
  { kind: "ai-idle", panel: 3 },
  { kind: "ai-click", panel: 3 },
  { kind: "ai-wave", panel: 3 },
  { kind: "ai-filled", panel: 3 },
  { kind: "hold" },
];

const PHASE_MS: Record<Phase["kind"], number> = {
  talk: 1050,
  "ai-idle": 900,
  "ai-click": 550,
  "ai-wave": 2500,
  "ai-filled": 800,
  hold: 3000,
};

const MD_QUERY = "(min-width: 768px)";
const AI_PURPLE = "#8077ff";

const SOAP_TABS = [
  { key: "S", label: "Subjective" },
  { key: "O", label: "Objective" },
  { key: "A", label: "Assessment" },
  { key: "P", label: "Plan" },
] as const;

const SUBJECTIVE =
  "Right shoulder pain, gradual onset over one month, progressively worsening. Dull ache, occasionally sharp, aggravated by overhead reaching.";
const SUB_WORDS = SUBJECTIVE.split(" ");
const SUB_CHIPS = ["Reason for Visit", "Primary Concern", "Pain Location", "Pain Description"];

const OBJ_ROWS = [
  { label: "Flexion", vals: ["142°", "150°"] },
  { label: "Abduction", vals: ["130°", "138°"] },
  { label: "Int. Rotation", vals: ["58°", "65°"] },
  { label: "Ext. Rotation", vals: ["70°", "78°"] },
];
const OBJ_OBS = [
  { k: "Palpation", v: "Tender over supraspinatus insertion" },
  { k: "Movement Analysis", v: "Painful arc 80–120° abduction" },
];

const ASSESSMENT =
  "Findings consistent with right rotator cuff tendinopathy and mild capsular restriction limiting overhead motion.";
const GOALS = [
  { k: "Short-Term", v: "Reduce pain to 2/10 in 2 wks" },
  { k: "Mid-Term", v: "Flexion 150° by week 4" },
  { k: "Long-Term", v: "Full overhead ROM by week 6" },
];
const HEP = ["Pendulum Swings", "Wall Slides"];

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MD_QUERY).matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia(MD_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}

function PurpleWaveform({ active }: { active: boolean }) {
  return (
    <span className="flex items-end gap-[1.5px] h-3">
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.span
          key={i}
          className="w-[2px] rounded-full"
          style={{ backgroundColor: AI_PURPLE, height: "40%" }}
          animate={
            active
              ? { height: ["30%", "100%", "45%", "80%", "35%"] }
              : { height: "40%" }
          }
          transition={{
            duration: 0.7,
            repeat: active ? Infinity : 0,
            delay: i * 0.08,
            ease: "easeInOut",
          }}
        />
      ))}
    </span>
  );
}

function AiHeaderAffordance({
  mode,
  showCursor,
}: {
  mode: "glitter" | "wave" | "hidden";
  showCursor: boolean;
}) {
  if (mode === "hidden") return null;

  return (
    <span className="relative ml-1.5 flex items-center shrink-0">
      <AnimatePresence mode="wait" initial={false}>
        {mode === "glitter" ? (
          <motion.span
            key="glitter"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.25 }}
            className="relative flex items-center justify-center"
          >
            <Sparkles className="w-3.5 h-3.5" style={{ color: AI_PURPLE }} />
            {showCursor && (
              <motion.span
                className="absolute -right-1 -bottom-2"
                initial={{ opacity: 0, x: 10, y: 8 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <MousePointer2
                  className="w-3.5 h-3.5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]"
                  fill="white"
                />
              </motion.span>
            )}
          </motion.span>
        ) : (
          <motion.span
            key="wave"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.25 }}
          >
            <PurpleWaveform active />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

export default function NotationCoverWidget() {
  const isDesktop = useIsDesktop();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const soapRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [line, setLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  const phase = PHASES[phaseIdx];
  const last = PHASES.length - 1;

  const talkEvent =
    phase.kind === "talk" ? phase.event : EVENTS.length - 1;
  const active: 0 | 1 | 2 | 3 =
    phase.kind === "talk"
      ? EVENTS[phase.event].target
      : phase.kind === "hold"
      ? 3
      : phase.panel;

  // Assessment fills once its AI waveform runs; Plan after its own.
  const assessFilled =
    phase.kind === "hold" ||
    (phase.kind === "ai-wave" && phase.panel === 2) ||
    (phase.kind === "ai-filled" && phase.panel === 2) ||
    (phase.kind !== "talk" && "panel" in phase && phase.panel === 3);

  const planFilled =
    phase.kind === "hold" ||
    (phase.kind === "ai-wave" && phase.panel === 3) ||
    (phase.kind === "ai-filled" && phase.panel === 3);

  const aiModeFor = (panel: 2 | 3): "glitter" | "wave" | "hidden" => {
    // Glitter from the start; waveform only while generating, held for 4s, then glitter again.
    if (phase.kind === "ai-wave" && phase.panel === panel) {
      return "wave";
    }
    return "glitter";
  };

  const showCursorOn = (panel: 2 | 3) =>
    (phase.kind === "ai-idle" || phase.kind === "ai-click") && phase.panel === panel;

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setPhaseIdx(last);
      return;
    }
    timeoutRef.current = window.setTimeout(
      () => setPhaseIdx((s) => (s >= last ? 0 : s + 1)),
      PHASE_MS[phase.kind]
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [phaseIdx, last, phase.kind]);

  useLayoutEffect(() => {
    const compute = () => {
      const c = containerRef.current;
      const t = transcriptRef.current;
      const p = isDesktop ? panelRefs.current[active] : soapRef.current;
      if (!c || !t || !p) return;
      const cr = c.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      const pr = p.getBoundingClientRect();
      setLine({
        x1: tr.right - cr.left,
        y1: tr.top + tr.height / 2 - cr.top,
        x2: pr.left - cr.left,
        y2: pr.top + pr.height / 2 - cr.top,
      });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [active, isDesktop, phaseIdx]);

  const fillFor = (panel: number) => {
    let f = 0;
    const end = Math.min(talkEvent, EVENTS.length - 1);
    for (let i = 0; i <= end; i++) {
      if (EVENTS[i].target === panel) f = Math.max(f, EVENTS[i].fill);
    }
    return f;
  };

  const subFill = fillFor(0);
  const subWordCount = Math.round((SUB_WORDS.length * subFill) / 4);
  const subText = SUB_WORDS.slice(0, subWordCount).join(" ");

  const visibleLines = EVENTS.slice(0, talkEvent + 1);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [talkEvent]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 p-3 sm:p-4 md:p-5 flex items-center gap-2.5 sm:gap-3 md:gap-4 pointer-events-none"
    >
      {line && (
        <svg className="absolute inset-0 w-full h-full z-0 overflow-visible pointer-events-none">
          <motion.path
            key={`${active}-${isDesktop ? "d" : "m"}-${phase.kind}`}
            d={`M ${line.x1} ${line.y1} C ${(line.x1 + line.x2) / 2} ${line.y1}, ${(line.x1 + line.x2) / 2} ${line.y2}, ${line.x2} ${line.y2}`}
            fill="none"
            stroke="#7DD3FC"
            strokeWidth={2}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <circle cx={line.x1} cy={line.y1} r={3} fill="#7DD3FC" />
          <motion.circle
            cx={line.x2}
            cy={line.y2}
            r={4}
            fill="#7DD3FC"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          />
        </svg>
      )}

      {/* Transcript */}
      <div
        ref={transcriptRef}
        className="relative z-10 shrink-0 h-[94%] w-[38%] max-w-[210px] rounded-2xl bg-white shadow-[0_16px_44px_-18px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col"
      >
        <div className="relative flex items-center justify-between px-2.5 py-2 bg-[#1F3D3B]">
          <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-[#486633]" />
          <span className="text-[11px] font-semibold text-white">Transcript</span>
          <span className="flex items-end gap-[2px] h-3">
            {[0, 1, 2, 3].map((i) => (
              <motion.span
                key={i}
                className="w-[3px] rounded-full bg-[#ef4444]"
                animate={{ height: ["30%", "100%", "45%"] }}
                transition={{
                  duration: 0.7,
                  repeat: Infinity,
                  delay: i * 0.12,
                  ease: "easeInOut",
                }}
                style={{ height: "30%" }}
              />
            ))}
          </span>
        </div>
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 px-2.5 py-2.5 flex flex-col justify-start gap-2 overflow-hidden"
        >
          <AnimatePresence initial={false}>
            {visibleLines.map((lineItem, i) => {
              const isClinician = lineItem.speaker === "Clinician";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`shrink-0 flex flex-col ${isClinician ? "items-start" : "items-end"}`}
                >
                  <div className={`max-w-[88%] ${isClinician ? "text-left" : "text-right"}`}>
                    <p
                      className={`text-[9px] font-semibold ${
                        isClinician ? "text-[#439CCB]" : "text-[#495B25]"
                      }`}
                    >
                      {lineItem.speaker}
                    </p>
                    <p className="text-[11px] leading-tight text-gray-600">{lineItem.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {isDesktop ? (
        <div className="relative z-10 flex-1 min-w-0 h-full flex flex-col gap-1.5">
          <SoapPanel
            panelRef={(el) => (panelRefs.current[0] = el)}
            title="Subjective"
            active={active === 0}
            grow={1}
          >
            <SubjectiveBody
              subText={subText}
              subFill={subFill}
              showCursor={subFill > 0 && subFill < 4}
            />
          </SoapPanel>

          <SoapPanel
            panelRef={(el) => (panelRefs.current[1] = el)}
            title="Objective"
            active={active === 1}
            grow={1.5}
          >
            <ObjectiveBody fill={fillFor(1)} />
          </SoapPanel>

          <SoapPanel
            panelRef={(el) => (panelRefs.current[2] = el)}
            title="Assessment"
            active={active === 2}
            grow={1.5}
            headerExtra={
              <AiHeaderAffordance
                mode={aiModeFor(2)}
                showCursor={showCursorOn(2)}
              />
            }
          >
            <AssessmentBody filled={assessFilled} />
          </SoapPanel>

          <SoapPanel
            panelRef={(el) => (panelRefs.current[3] = el)}
            title="Plan"
            active={active === 3}
            grow={1}
            headerExtra={
              <AiHeaderAffordance
                mode={aiModeFor(3)}
                showCursor={showCursorOn(3)}
              />
            }
          >
            <PlanBody filled={planFilled} />
          </SoapPanel>
        </div>
      ) : (
        <div
          ref={soapRef}
          className="relative z-10 flex-1 min-w-0 h-[94%] rounded-2xl bg-white/95 shadow-[0_16px_44px_-18px_rgba(0,0,0,0.55)] overflow-hidden flex flex-col"
        >
          <div className="relative shrink-0 grid grid-cols-4 bg-white">
            {SOAP_TABS.map((tab, i) => {
              const isActive = active === i;
              return (
                <div
                  key={tab.key}
                  className={`relative flex items-center justify-center py-2 transition-colors duration-300 ${
                    isActive ? "bg-[#486633]" : "bg-white"
                  } ${i === 0 ? "rounded-tl-2xl" : ""} ${i === 3 ? "rounded-tr-2xl" : ""}`}
                >
                  <span
                    className={`text-[12px] font-bold font-productSans ${
                      isActive ? "text-white" : "text-[#1F3D3B]"
                    }`}
                  >
                    {tab.key}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="soap-lip"
                      className="absolute left-1/2 -translate-x-1/2 -bottom-[6px] z-10"
                      transition={{ type: "spring", stiffness: 420, damping: 28 }}
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: "7px solid transparent",
                        borderRight: "7px solid transparent",
                        borderTop: "7px solid #486633",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="min-h-0 flex-1 overflow-hidden px-3 py-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.28 }}
                className="h-full"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-[11px] font-bold text-[#1F3D3B]">
                    {SOAP_TABS[active].label}
                  </p>
                  {(active === 2 || active === 3) && (
                    <AiHeaderAffordance
                      mode={aiModeFor(active)}
                      showCursor={showCursorOn(active)}
                    />
                  )}
                </div>
                {active === 0 && (
                  <SubjectiveBody
                    subText={subText}
                    subFill={subFill}
                    showCursor={subFill > 0 && subFill < 4}
                  />
                )}
                {active === 1 && <ObjectiveBody fill={fillFor(1)} />}
                {active === 2 && <AssessmentBody filled={assessFilled} />}
                {active === 3 && <PlanBody filled={planFilled} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}

function SubjectiveBody({
  subText,
  subFill,
  showCursor,
}: {
  subText: string;
  subFill: number;
  showCursor: boolean;
}) {
  return (
    <>
      <p className="text-[9px] leading-snug text-gray-600 mb-1.5">
        {subText}
        {showCursor && (
          <motion.span
            className="inline-block w-[3px] h-[9px] ml-[1px] align-middle bg-[#439CCB]"
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.7, repeat: Infinity, repeatType: "reverse" }}
          />
        )}
      </p>
      <div className="flex flex-wrap gap-1">
        {SUB_CHIPS.map((chip, i) => {
          const on = i < subFill;
          return (
            <span
              key={chip}
              className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-medium transition-colors duration-300 ${
                on ? "bg-[#486633] text-white" : "bg-gray-100 text-gray-400"
              }`}
            >
              <Check className={`w-2.5 h-2.5 ${on ? "opacity-100" : "opacity-30"}`} />
              {chip}
            </span>
          );
        })}
      </div>
    </>
  );
}

function ObjectiveBody({ fill }: { fill: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      {OBJ_ROWS.map((row, r) => {
        const on = r < fill;
        return (
          <div key={row.label} className="grid grid-cols-[1fr_auto_auto] gap-1 items-center">
            <span className="text-[9px] font-medium text-gray-600">{row.label}</span>
            {row.vals.map((v, cIdx) => (
              <span
                key={cIdx}
                className={`w-[38px] h-[16px] rounded flex items-center justify-center gap-0.5 transition-colors duration-300 ${
                  on ? "bg-[#f6faf7] ring-1 ring-[#486633]/25" : "bg-gray-50"
                }`}
              >
                {on ? (
                  <>
                    <span className="text-[9px] font-bold text-gray-900">{v}</span>
                    <Play className="w-2 h-2 text-[#486633] fill-[#486633]" />
                  </>
                ) : (
                  <span className="w-3.5 h-[3px] rounded bg-gray-200" />
                )}
              </span>
            ))}
          </div>
        );
      })}
      <div
        className={`mt-1 flex flex-col gap-0.5 transition-opacity duration-300 ${
          fill >= 4 ? "opacity-100" : "opacity-30"
        }`}
      >
        {OBJ_OBS.map((o) => (
          <p key={o.k} className="text-[9px] leading-tight text-gray-600">
            <span className="font-bold text-[#495B25]">{o.k}: </span>
            {o.v}
          </p>
        ))}
      </div>
    </div>
  );
}

function AssessmentBody({ filled }: { filled: boolean }) {
  return (
    <AnimatePresence mode="wait">
      {filled ? (
        <motion.div
          key="filled"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <p className="text-[9px] leading-snug text-gray-600 mb-1.5">{ASSESSMENT}</p>
          <p className="text-[9px] font-bold text-[#1F3D3B] mb-0.5">Goals</p>
          <div className="flex flex-col gap-0.5">
            {GOALS.map((g) => (
              <div key={g.k} className="flex items-start gap-1">
                <span className="text-[9px] text-[#486633] leading-tight">•</span>
                <p className="text-[9px] leading-tight text-gray-600">
                  <span className="font-bold text-[#495B25]">{g.k}: </span>
                  {g.v}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="h-full min-h-[48px]"
        />
      )}
    </AnimatePresence>
  );
}

function PlanBody({ filled }: { filled: boolean }) {
  return (
    <AnimatePresence mode="wait">
      {filled ? (
        <motion.div
          key="filled"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <p className="text-[9px] text-gray-600 mb-1.5">Continue therapy 2×/week.</p>
          <p className="text-[9px] font-bold text-[#1F3D3B] mb-0.5">HEP</p>
          <div className="flex flex-col gap-0.5">
            {HEP.map((h) => (
              <div key={h} className="flex items-start gap-1">
                <span className="text-[9px] text-[#486633] leading-tight">•</span>
                <p className="text-[9px] leading-tight text-gray-600">{h}</p>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="h-full min-h-[40px]"
        />
      )}
    </AnimatePresence>
  );
}

const SoapPanel = ({
  panelRef,
  title,
  active,
  grow = 1,
  headerExtra,
  children,
}: {
  panelRef: (el: HTMLDivElement | null) => void;
  title: string;
  active: boolean;
  grow?: number;
  headerExtra?: ReactNode;
  children: ReactNode;
}) => (
  <div
    ref={panelRef}
    style={{ flexGrow: grow, flexBasis: 0 }}
    className={`min-h-0 rounded-2xl bg-white/95 overflow-hidden flex flex-col transition-all duration-300 ${
      active
        ? "shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] ring-1 ring-[#7DD3FC]/50"
        : "shadow-[0_6px_20px_-12px_rgba(0,0,0,0.45)]"
    }`}
  >
    <div className="relative shrink-0 flex items-center px-2.5 py-1.5 bg-[#1F3D3B]">
      <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-[#486633]" />
      <p className="text-[11px] font-bold text-white">{title}</p>
      {headerExtra}
    </div>
    <div className="min-h-0 flex-1 overflow-hidden px-2.5 py-1.5">{children}</div>
  </div>
);
